import PrimaryNavigation from '../../components/Gnb/PrimaryNav';
import MobileMenuDrawer from '../../components/Gnb/MobileMenuDrawer';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import iconMenu20 from '@repo/common/assets/icons/20px/icon_20px_hamburger-menu.svg';
import iconMenu32 from '@repo/common/assets/icons/32px/icon_32px_hamburger-menu.svg';
import iconCancel16 from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import {
  applyTypography,
  SCREENS,
  Logo,
  UserBadge,
  useMediaQuery,
  SocialLinks,
  Header,
  Shell,
  Inner,
  ScrolledInner,
  ScrolledRow,
  ScrolledNavWrap,
  TopRow,
  BottomRow,
  LeftWrap,
  MenuButton,
  useAuth,
  useLoginRequiredModal,
} from '@repo/common';
import logoLightAdmin from '@repo/common/assets/logos/koready/m/logo-light-admin.svg';
import logoLightEmployers from '@repo/common/assets/logos/koready/m/logo-light-employers.svg';
import logoBlackAdmin from '@repo/common/assets/logos/m/logo-black-admin.svg';
import logoBlackEmployers from '@repo/common/assets/logos/m/logo-black-employers.svg';
import RightControls from '../../components/Gnb/RightControls';
import { getNavItems } from '../../components/Gnb/jobNavigationConfig';
import { IS_LEGACY_DOMAIN } from '../../utils/brand.js';

const GNB_BRAND = IS_LEGACY_DOMAIN ? 'oing' : 'koready';
const BRAND_NAME = IS_LEGACY_DOMAIN ? 'Oinglab' : 'Koready';

const ADMIN_ROLES = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'];
const BUSINESS_ROLES = [
  'E',
  'ROLE_E',
  'EMPLOYER',
  'ROLE_EMPLOYER',
  'EMPLOYEE',
  'ROLE_EMPLOYEE',
];

// 메뉴 버튼은 tabletV 미만에서만 보인다. tabletH 구간은 32px, 그 아래는 20px 아이콘
// (Tailwind 브레이크포인트와 맞추려고 CSS 로 전환한다)
const MenuIcon = ({ isOpen }) =>
  isOpen ? (
    <img src={iconCancel16} alt='' aria-hidden='true' loading='lazy' />
  ) : (
    <>
      <img
        src={iconMenu20}
        alt=''
        aria-hidden='true'
        loading='lazy'
        className='tabletH:hidden'
      />
      <img
        src={iconMenu32}
        alt=''
        aria-hidden='true'
        loading='lazy'
        className='hidden tabletH:block'
      />
    </>
  );

const getActivePathFor = (pathname, navItems) => {
  const current = navItems.find(({ href }) => {
    const path = (href || '').split('?')[0];
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  });

  return current ? current.href : null;
};

function GNB() {
  const { user } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const { pathname } = useLocation();
  const [activePath, setActivePath] = useState(() =>
    getActivePathFor(pathname, getNavItems(user?.role)),
  );

  // GNB 는 페이지 이동 후에도 언마운트되지 않으므로, 이전에 클릭한 메뉴가
  // 무관한 페이지(My Profile 등)에서 밑줄로 남지 않도록 이동할 때마다 다시 계산한다
  useEffect(() => {
    setActivePath(getActivePathFor(pathname, getNavItems(user?.role)));
  }, [pathname, user?.role]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollThreshold, setScrollThreshold] = useState(40);
  const defaultInnerRef = useRef(null);
  const scrolledMinWidth = SCREENS.tabletV;
  const isTabletVOnly = useMediaQuery(
    `(min-width: ${SCREENS.tabletV}px) and (max-width: ${
      SCREENS.desktopL - 0.02
    }px)`,
  );
  const canUseScrolledLayout = useMediaQuery(
    `(min-width: ${scrolledMinWidth}px)`,
  );
  const isScrolledLayout = canUseScrolledLayout && isScrolled;
  const normalizedRole = String(user?.role ?? '').toUpperCase();
  const isAdmin = ADMIN_ROLES.includes(normalizedRole);
  const isBusiness = BUSINESS_ROLES.includes(normalizedRole);
  const roleLabel = isAdmin ? 'Admin' : isBusiness ? 'for Business' : '';
  const roleLogoSrc = isAdmin
    ? IS_LEGACY_DOMAIN
      ? logoBlackAdmin
      : logoLightAdmin
    : isBusiness
      ? IS_LEGACY_DOMAIN
        ? logoBlackEmployers
        : logoLightEmployers
      : null;
  // 아래 2단 로고 에셋은 'for Employers' 문구가 path로 박혀 있어 alt도 에셋 기준.
  // 디자인에서 'for Business' 버전 export 나오면 에셋/alt 함께 교체 필요.
  const roleLogoAlt = isAdmin
    ? `${BRAND_NAME} Admin logo`
    : isBusiness
      ? `${BRAND_NAME} Employers logo`
      : `${BRAND_NAME} logo`;
  const shouldUseRoleLogo =
    isScrolledLayout && isTabletVOnly && Boolean(roleLogoSrc);

  const handleLoginRequired = useCallback(() => {
    showLoginModal('gnb_login_button');
  }, [showLoginModal]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleMenuButtonClick = useCallback((event) => {
    event.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateThreshold = () => {
      const nextThreshold = defaultInnerRef.current?.offsetHeight;
      if (typeof nextThreshold === 'number' && nextThreshold > 0) {
        setScrollThreshold(nextThreshold);
      }
    };

    const handleScroll = () => {
      const nextScrolled = window.scrollY >= scrollThreshold;
      setIsScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));
    };

    updateThreshold();
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThreshold);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThreshold);
    };
  }, [scrollThreshold]);

  return (
    <>
      <MobileMenuDrawer
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        activePath={activePath}
        onLoginRequired={handleLoginRequired}
      />
      <Header
        data-scrolled={isScrolledLayout ? 'true' : undefined}
        data-fixed='true'
      >
        <Shell data-mode={isScrolledLayout ? 'scrolled' : undefined}>
          {isScrolledLayout ? (
            <ScrolledInner>
              <ScrolledRow>
                <LeftWrap>
                  <MenuButton
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    onClick={handleMenuButtonClick}
                    className={
                      isMenuOpen
                        ? 'rounded-lg bg-grayscale-gray02 [&>img]:!h-4 [&>img]:!w-4'
                        : ''
                    }
                  >
                    <MenuIcon isOpen={isMenuOpen} />
                  </MenuButton>
                  <div className='inline-flex items-center gap-[2px]'>
                    <Logo
                      mode='scrolled'
                      brand={GNB_BRAND}
                      srcOverride={shouldUseRoleLogo ? roleLogoSrc : undefined}
                      alt={shouldUseRoleLogo ? roleLogoAlt : `${BRAND_NAME} logo`}
                    />
                    <UserBadge badgeName={user?.badgeName} />
                  </div>
                  {!shouldUseRoleLogo && roleLabel && (
                    <span
                      className={`text-text-primary ${applyTypography(
                        'b16Strong',
                      )}`}
                    >
                      {roleLabel}
                    </span>
                  )}
                  {!shouldUseRoleLogo && isBusiness && (
                    <span
                      className={`inline-flex items-center justify-center px-1 py-0.5 tabletV:h-6 tabletV:px-2 tabletV:py-0 rounded-[8px] bg-purple-purple01 text-purple-purple05 ${applyTypography(
                        'b12Strong',
                      )}`}
                    >
                      Beta
                    </span>
                  )}
                </LeftWrap>

                <ScrolledNavWrap>
                  <PrimaryNavigation
                    variant='scrolled'
                    activePath={activePath}
                    onSelect={setActivePath}
                    onLoginRequired={handleLoginRequired}
                  />
                </ScrolledNavWrap>

                <RightControls mode='scrolled' />
              </ScrolledRow>
            </ScrolledInner>
          ) : (
            <Inner ref={defaultInnerRef}>
              <TopRow>
                <LeftWrap>
                  <MenuButton
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    onClick={handleMenuButtonClick}
                    className={
                      isMenuOpen
                        ? 'rounded-lg bg-grayscale-gray02 [&>img]:!h-4 [&>img]:!w-4'
                        : ''
                    }
                  >
                    <MenuIcon isOpen={isMenuOpen} />
                  </MenuButton>
                  <div className='inline-flex items-center gap-1 tabletH:gap-2'>
                    <div className='inline-flex items-center gap-[2px]'>
                      <Logo brand={GNB_BRAND} />
                      <UserBadge badgeName={user?.badgeName} />
                    </div>
                    {roleLabel && (
                      <span className='inline-flex items-center gap-[2px] whitespace-nowrap tabletH:gap-1 tabletV:gap-2'>
                        <span className='text-text-primary typo-b13Strong mobile:typo-b14Strong tabletH:typo-b16Strong'>
                          {roleLabel}
                        </span>
                        {isBusiness && (
                          <>
                            {/* tabletH 미만은 칩 대신 보조 텍스트로 표시 */}
                            <span className='text-text-secondary typo-b11 tabletH:hidden'>
                              (Beta)
                            </span>
                            <span
                              className={`hidden tabletH:inline-flex items-center justify-center px-1 py-0.5 tabletV:h-6 tabletV:px-2 tabletV:py-0 rounded-full tabletV:rounded-[8px] bg-purple-purple01 text-purple-purple05 ${applyTypography(
                                'b12Strong',
                              )}`}
                            >
                              Beta
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </LeftWrap>
                <RightControls />
              </TopRow>

              <BottomRow>
                <PrimaryNavigation
                  activePath={activePath}
                  onSelect={setActivePath}
                  onLoginRequired={handleLoginRequired}
                />
                <SocialLinks />
              </BottomRow>
            </Inner>
          )}
        </Shell>
      </Header>
      <div aria-hidden='true' className='shrink-0 bg-grayscale-gray01' style={{ height: scrollThreshold }} />
    </>
  );
}

export default GNB;
